import { gsap } from 'gsap';
import type { ThreeStage } from '../../../../three/ThreeStage';
import { BaseScene } from '../../../../core/BaseScene';
import { glyphGroup, type GlyphId } from '../../../../components/glyphs';
import { draw } from '../../../../components/svg';
import { words, revealWords, settleWords } from '../../../../components/text';
import { productReveal, playProductReveal, settleProductReveal } from '../../../../components/reveal';

/**
 * Areas of a technology organisation that carry an environmental footprint (MINT-IT.md §4, §10),
 * each with the kind of scattered record its data usually lives in today.
 */
const AREAS: ReadonlyArray<{ label: string; glyph: GlyphId; gx: number; gy: number; record: string }> = [
  { label: 'Offices', glyph: 'building', gx: 0, gy: 0, record: 'electricity bill.pdf' },
  { label: 'Cloud', glyph: 'cloud', gx: 1, gy: 0, record: 'cloud usage export' },
  { label: 'Data centres', glyph: 'server', gx: 2, gy: 0, record: 'generator fuel log' },
  { label: 'Software', glyph: 'code', gx: 0, gy: 1, record: 'hosting invoices' },
  { label: 'Devices', glyph: 'device', gx: 1, gy: 1, record: 'asset register.xlsx' },
  { label: 'Networks', glyph: 'network', gx: 2, gy: 1, record: 'equipment list' },
  { label: 'Procurement', glyph: 'box', gx: 0, gy: 2, record: 'supplier sheet v3' },
  { label: 'E-waste', glyph: 'recycle', gx: 1, gy: 2, record: 'disposal notes' },
  { label: 'Water', glyph: 'drop', gx: 2, gy: 2, record: 'meter readings' },
];

const ORIGIN = { x: 620, y: 236 };
const iso = (gx: number, gy: number) => ({ x: ORIGIN.x + (gx - gy) * 150, y: ORIGIN.y + (gx + gy) * 88 });

/** MINT-IT's workflow (MINT-IT.md §10), with the data-integrity lifecycle behind VERIFY (§5). */
const WORKFLOW = ['Collect', 'Calculate', 'Verify', 'Understand', 'Report', 'Improve'];
const VERIFY_DETAIL = 'entry · verification · approval · audit trail';
const CHAIN_X = 1250;
const chainY = (i: number): number => 176 + i * 96;

/** The platform's direction (MINT-IT.md §1, §6, §7). Only phase 1 is presented as the current foundation. */
const ROADMAP = [
  { title: 'Organisation', detail: 'Organisational sustainability', state: 'now' },
  { title: 'Software products', detail: 'Product-level footprint', state: 'next' },
  { title: 'Readiness', detail: 'Certification & compliance readiness', state: 'next' },
];
const ROAD_Y = 440;
const roadX = (i: number): number => 380 + i * 420;

const LINES = ['MEASURE IT.', 'UNDERSTAND IT.', 'IMPROVE IT.'];

/**
 * 0 The technology organisation — TECHNOLOGY HAS AN ENVIRONMENTAL FOOTPRINT.
 * 1 Its data is scattered across files and spreadsheets — MEASURING IT SHOULD NOT BE FRAGMENTED.
 * 2 MINT-IT — Sustainability intelligence for technology organisations.
 * 3 Collect → calculate → verify → understand → report → improve; the scattered records become structured
 * 4 Organisation → software products → readiness
 * 5 MEASURE IT. UNDERSTAND IT. IMPROVE IT.
 */
export class MintItScene extends BaseScene {
  public readonly stepCount = 6;

  protected template(): string {
    const links: string[] = [];
    AREAS.forEach((a) => {
      const p = iso(a.gx, a.gy);
      if (a.gx < 2) { const q = iso(a.gx + 1, a.gy); links.push(`M${p.x} ${p.y} L${q.x} ${q.y}`); }
      if (a.gy < 2) { const q = iso(a.gx, a.gy + 1); links.push(`M${p.x} ${p.y} L${q.x} ${q.y}`); }
    });

    const tiles = AREAS.map((a, i) => {
      const p = iso(a.gx, a.gy);
      const tilt = ((i * 37) % 9) - 4;
      return `<g class="me-asset" data-area="${i}" transform="translate(${p.x} ${p.y})">
        <g class="me-asset__motion">
          <path class="me-tile__side" d="M-110 0 L0 64 L110 0 L110 14 L0 78 L-110 14 Z"/>
          <path class="me-tile" d="M0 -64 L110 0 L0 64 L-110 0 Z"/>
          <g class="me-asset__glyph" transform="translate(0 -22)">${glyphGroup(a.glyph, 34)}</g>
          <text class="me-asset__label" y="26" text-anchor="middle">${a.label}</text>
          <g transform="translate(0 -84)"><g class="mit-tag" data-tilt="${tilt}">
            <rect x="-86" y="-15" width="172" height="30" rx="7"/>
            <text y="5" text-anchor="middle">${a.record}</text>
          </g></g>
        </g>
      </g>`;
    }).join('');

    const chain = WORKFLOW.map((label, i) => `
      <g class="me-chain__item" data-index="${i}">
        ${i > 0 ? `<path class="me-chain__link" d="M${CHAIN_X} ${chainY(i - 1) + 16} V${chainY(i) - 26}"/>` : ''}
        <circle class="me-chain__dot" cx="${CHAIN_X}" cy="${chainY(i)}" r="7"/>
        <text class="me-chain__label" x="${CHAIN_X + 26}" y="${chainY(i) + 8}">${label.toUpperCase()}</text>
        ${label === 'Verify' ? `<text class="mit-verify" x="${CHAIN_X + 26}" y="${chainY(i) + 32}">${VERIFY_DETAIL}</text>` : ''}
      </g>`).join('');

    const road = ROADMAP.map((r, i) => `
      <g class="mit-road__stage mit-road__stage--${r.state}" data-index="${i}">
        ${i > 0 ? `<path class="mit-road__link draw" pathLength="1" d="M${roadX(i - 1) + 58} ${ROAD_Y} H${roadX(i) - 58}"/>` : ''}
        <circle class="mit-road__ring" cx="${roadX(i)}" cy="${ROAD_Y}" r="44"/>
        <circle class="mit-road__dot" cx="${roadX(i)}" cy="${ROAD_Y}" r="10"/>
        <text class="mit-road__title" x="${roadX(i)}" y="${ROAD_Y + 88}" text-anchor="middle">${r.title.toUpperCase()}</text>
        <text class="mit-road__detail" x="${roadX(i)}" y="${ROAD_Y + 116}" text-anchor="middle">${r.detail}</text>
        <text class="mit-road__state" x="${roadX(i)}" y="${ROAD_Y - 66}" text-anchor="middle">${r.state === 'now' ? 'FOUNDATION' : 'DIRECTION'}</text>
      </g>`).join('');

    return `
      <div class="scene-layout mint-it">
        <svg class="diagram mit-diagram" viewBox="0 0 1600 900" role="img" aria-label="The environmental footprint of a technology organisation, structured by MINT-IT: ${WORKFLOW.join(', ')}">
          <g class="mit-estate">
            <path class="me-links draw" pathLength="1" d="${links.join(' ')}"/>
            ${tiles}
          </g>
          <g class="me-chain" data-from="3">${chain}</g>
          <g class="mit-road" data-from="4">${road}</g>
        </svg>
        <div class="mit-statements">
          <h2 class="statement" data-from="0" data-until="0">${words('TECHNOLOGY HAS AN ENVIRONMENTAL FOOTPRINT.')}</h2>
          <h2 class="statement statement--accent" data-from="1" data-until="1">${words('MEASURING IT SHOULD NOT BE FRAGMENTED.')}</h2>
          <div class="mit-closing" data-from="5">${LINES.map((l, i) => `<h2 class="statement${i === 2 ? ' statement--accent' : ''}">${words(l)}</h2>`).join('')}</div>
        </div>
        ${productReveal('mint-it', 2, '', 2)}
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    const order = AREAS.map((a, i) => ({ i, depth: a.gx + a.gy })).sort((p, q) => p.depth - q.depth);
    order.forEach(({ i }, n) => {
      tl.fromTo(this.$(`.me-asset[data-area="${i}"] .me-asset__motion`), { opacity: 0, y: 46 }, { opacity: 1, y: 0, duration: 0.9, ease: 'expo.out' }, 0.15 + n * 0.12);
    });
    tl.add(draw(this.$('.me-links'), true, { duration: 1.4 }), 1.1);
    tl.add(revealWords(this.$('.mit-statements .statement')), 1.3);
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode(this.meta.id, step, immediate);
    const forward = !immediate && step > previousStep;
    const d = immediate ? 0 : 1;
    const tl = this.timeline();

    gsap.set(this.$$('.me-asset__motion'), { opacity: 1, y: 0 });
    draw(this.$('.me-links'), true, { immediate: true });
    this.$$('.mit-statements .statement').forEach((s) => settleWords(s));

    // Records: scattered and tilted while fragmented, squared up and connected once structured.
    const tags = this.$$('.mit-tag');
    const structured = step >= 3;
    const tagsVisible = step === 1 || step === 3;
    this.$('.mit-diagram').dataset.structured = String(structured);
    tags.forEach((tag, i) => {
      const tilt = Number(tag.dataset.tilt);
      const target = { opacity: tagsVisible ? 1 : 0, rotation: structured ? 0 : tilt, y: 0, transformOrigin: '50% 50%' };
      if (forward && step === 1) tl.fromTo(tag, { opacity: 0, y: 14, rotation: tilt * 2 }, { ...target, duration: 0.6, ease: 'back.out(1.6)' }, 0.3 + i * 0.08);
      else if (forward && step === 3) tl.to(tag, { ...target, duration: 0.7, ease: 'power3.out' }, 0.4 + i * 0.05);
      else gsap.to(tag, { ...target, duration: d * 0.5, overwrite: 'auto' });
    });
    if (forward && step === 1) tl.add(revealWords(this.$$('.mit-statements .statement')[1]), 0.1);

    // The estate recedes while the product is named, and again while the roadmap and closing lines speak.
    const estateOpacity = step === 2 ? 0.2 : step >= 4 ? 0.08 : 1;
    gsap.to(this.$('.mit-estate'), { opacity: estateOpacity, duration: d * 0.9, ease: 'power2.inOut' });
    gsap.to(this.$('.me-chain'), { opacity: step >= 4 ? 0 : 1, duration: d * 0.9, ease: 'power2.inOut' });
    if (forward && step === 2) playProductReveal(this.root, tl, 0.5);
    else settleProductReveal(this.root);

    // The workflow lights one stage at a time.
    this.$$('.me-chain__item').forEach((item, i) => {
      if (forward && step === 3) {
        item.classList.remove('is-lit');
        tl.call(() => item.classList.add('is-lit'), [], 0.3 + i * 0.35);
      } else {
        item.classList.toggle('is-lit', step >= 3);
      }
    });

    // The direction: from the organisation, to the software it builds, to readiness.
    const roadLinks = this.$$('.mit-road__link');
    const stages = this.$$('.mit-road__stage');
    if (forward && step === 4) {
      tl.fromTo(stages, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.7, stagger: 0.5, ease: 'power3.out' }, 0.4);
      draw(roadLinks, false, { immediate: true });
      roadLinks.forEach((link, i) => tl.add(draw(link, true, { duration: 0.5 }), 0.8 + i * 0.5));
    } else {
      gsap.set(stages, { opacity: 1, y: 0 });
      draw(roadLinks, step >= 4, { immediate: true });
    }
    gsap.to(this.$('.mit-road'), { y: step >= 5 ? -60 : 0, duration: d * 0.8, ease: 'power2.inOut' });

    if (forward && step === 5) this.$$('.mit-closing .statement').forEach((s, i) => tl.add(revealWords(s), 0.2 + i * 0.5));
  }
}
